import {
    useState, useRef, useEffect,
    JSX
} from 'react';
import {
    Bot, Send, Loader2, MessageSquare
} from 'lucide-react';
import {
    Button
} from 'shared/components/ui/button';
import {
    ScrollArea
} from 'shared/components/ui/scroll-area';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    assistantWsService
} from 'shared/api/services/PROJECT-O/WEBSOCKETS/Assistant.websocket.service';
import {
    useParams
} from 'react-router-dom';

// Define types for the message structure
interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
}

interface ChatSectionProps {
    onResponseReceived?: (response: string) => void;
}

export default function ChatSection({
    onResponseReceived
}: ChatSectionProps): JSX.Element {
    const {
 oragID, subOragID
} = useParams<{ oragID: string, subOragID: string }>();

    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            role: 'assistant',
            content: "Hello! I'm your AI assistant. I can help you create and design your page. What would you like to do?",
        },
    ]);
    const [input, setInput] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const messagesEndRef = useRef<HTMLDivElement | null>(null);

    // Function to automatically scroll to the latest message
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({
            behavior: 'smooth'
        });
    }, [messages]);

    const handleSendMessage = async (): Promise<void> => {
    if (!input.trim()) return;

    const userMsgObject: Message = {
        role: 'user',
        content: input.trim(),
        id: (Date.now()).toString(),
    };

    // Add user message to chat
    setMessages((prev) => [...prev, userMsgObject]);
    setInput('');
    setIsLoading(true);

    try {
        assistantWsService.setAssistant('686673d92213d543f821e41b');
        const response = await assistantWsService.sendToAssistant(oragID, subOragID, 'OpCodeBuilder', {
            real_time_data: input.trim(),
        });

        // Ensure response and data are valid before using them
        const data = response as { result?: { execution_log: any[] } }; // Type assertion

        // Safely extract llmResponse and ensure it's a string
        const llmResponse = (() => {
            const output = data?.result?.execution_log?.slice(-1)[0]?.Outputs;
            if (output && Object.values(output)[0]) {
                const result = Object.values(output)[0];
                return typeof result === 'string' ? result : 'An error occurred after execution. No result was generated.';
            }
            return 'An error occurred after execution. No result was generated.';
        })();

        const assistantMessage: Message = {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content: 'Schema generated! You can view the details of the generated schema on the Database Schema tab or the Preview Tab.',
        };

        setMessages((prev) => [...prev, assistantMessage]);

        if (onResponseReceived) {
            onResponseReceived(llmResponse); // Pass both llm string and full object
        }

    } catch (error) {
        console.error('Error in handleSendMessage:', error);
        setMessages((prev) => [
            ...prev,
            {
                id: (Date.now() + 2).toString(),
                role: 'assistant',
                content: 'Something went wrong. Please try again.'
            },
        ]);
    } finally {
        setIsLoading(false);
    }
};
    return (
        <div className="w-1/3 flex flex-col border-r bg-background h-full">
            <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-xl font-bold">AI Assistant</h2>
                <Button variant="outline">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Chat
                </Button>
            </div>

            <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                    {messages.map((message) => (
                        <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div
                                className={`rounded-lg p-4 max-w-[80%] ${
                                    message.role === 'user'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-muted text-muted-foreground'
                                }`}
                            >
                                {message.role === 'assistant' && (
                                    <div className="flex items-center gap-2 mb-2">
                                        <Bot className="h-5 w-5" />
                                        <span className="font-semibold">AI Assistant</span>
                                    </div>
                                )}
                                <p className="text-sm">{message.content}</p>
                            </div>
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
            </ScrollArea>

            <div className="p-4 border-t">
                <Textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Describe what you want to create..."
                    rows={3}
                    className="mb-2 resize-none"
                />
                <Button onClick={handleSendMessage} disabled={isLoading} className="w-full">
                    {isLoading ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Please wait
                        </>
                    ) : (
                        <>
                            <Send className="mr-2 h-4 w-4" />
                            Send
                        </>
                    )}
                </Button>
            </div>
        </div>
    );
}